import React, { useState, useMemo } from 'react';
import { useGetPayersQuery } from '../../../../services/ConfigurationService';
import { PayerType } from '../../../../services/ConfigurationService/types/payerType';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Page, Sort, RowSelectEventArgs } from '@syncfusion/ej2-react-grids';
import { EditPayerDialog } from '../dialogs/EditPayerDialog';
import { Box, Typography, TextField, Stack, IconButton } from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';

interface PayerGridRow {
  id: string;
  payerName: string;
  payerCode: string;
  evaEnabled: boolean;
  payerGroup: string;
  financialClass: string;
  ecsPayerId: string;
  waystarAuthPayer: string;
}

interface PayerGridProps {
  company: string;
  centerId: string;
}

export const PayerGrid: React.FC<PayerGridProps> = ({ company, centerId }) => {
  const { data: payers = [], isLoading, error } = useGetPayersQuery({ company, centerId }, { skip: !company || !centerId });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPayer, setSelectedPayer] = useState<PayerType | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  // Filter payers based on search term
  const filteredPayers = useMemo(() => {
    if (!searchTerm.trim()) {
      return payers;
    }
    
    const searchLower = searchTerm.toLowerCase();
    return payers.filter(payer => 
      (payer.payerName?.toLowerCase().includes(searchLower)) ||
      (payer.payerCode?.toLowerCase().includes(searchLower)) ||
      ((payer.evaEnabled ? 'enabled' : 'disabled').includes(searchLower)) ||
      (payer.financialClass?.toLowerCase().includes(searchLower)) ||
      (payer.ecsPayerId?.toLowerCase().includes(searchLower)) ||
      (payer.waystarAuthPayer?.toLowerCase().includes(searchLower))
    );
  }, [payers, searchTerm]);

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  // Handle row selection for editing
  const handleRowSelection = (args: RowSelectEventArgs) => {
    if (args.data) {
      const selectedRow = args.data as PayerGridRow;
      const payer = payers.find(p => p.id === selectedRow.id);
      if (payer) {
        setSelectedPayer(payer);
        // Trigger the dialog by clicking the hidden button
        setTimeout(() => {
          triggerRef.current?.click();
        }, 0);
      }
    }
  };

  const handleEditDialogClose = () => {
    setSelectedPayer(null);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <Typography>Loading payers...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="error">
          Error loading payers. Please try again.
        </Typography>
      </Box>
    );
  }

  if (payers.length === 0) {
    return (
      <Box>
        {/* Search Row */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SearchIcon sx={{ color: '#64748b' }} />
            <TextField
              fullWidth
              size="small"
              placeholder="Search by payer name, code, financial class..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#3b82f6',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3b82f6',
                  }
                }
              }}
            />
            {searchTerm && (
              <IconButton
                size="small"
                onClick={handleClearSearch}
                sx={{ color: '#64748b' }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Box>
        
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
            No payers found
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            No payers available for this center
          </Typography>
        </Box>
      </Box>
    );
  }

  // Handle no search results
  if (filteredPayers.length === 0 && searchTerm.trim()) {
    return (
      <Box>
        {/* Search Row */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SearchIcon sx={{ color: '#64748b' }} />
            <TextField
              fullWidth
              size="small"
              placeholder="Search by payer name, code, financial class..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#3b82f6',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3b82f6',
                  }
                }
              }}
            />
            {searchTerm && (
              <IconButton
                size="small"
                onClick={handleClearSearch}
                sx={{ color: '#64748b' }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Box>
        
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
            No payers match your search
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            Try adjusting your search term: "{searchTerm}"
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Search Row */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <SearchIcon sx={{ color: '#64748b' }} />
          <TextField
            fullWidth
            size="small"
            placeholder="Search by payer name, code, financial class..."
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#ffffff',
                '& fieldset': {
                  borderColor: '#e2e8f0',
                },
                '&:hover fieldset': {
                  borderColor: '#3b82f6',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#3b82f6',
                }
              }
            }}
          />
          {searchTerm && (
            <IconButton
              size="small"
              onClick={handleClearSearch}
              sx={{ color: '#64748b' }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </Box>

      {/* Grid */}
      <GridComponent
        dataSource={filteredPayers}
        height={500}
        allowPaging={true}
        allowSorting={true}
        pageSettings={{ pageSize: 25 }}
        selectionSettings={{ mode: 'Row', type: 'Single' }}
        rowSelected={handleRowSelection}
        style={{
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          backgroundColor: '#ffffff'
        }}
        enableHover={true}
      >
          <ColumnsDirective>
            <ColumnDirective field="payerName" headerText="Payer Name" width="200" textAlign="Left" />
            <ColumnDirective field="payerCode" headerText="Payer Code" width="120" textAlign="Left" />
            <ColumnDirective field="evaEnabled" headerText="EVA Enabled" width="120" textAlign="Left" />
            <ColumnDirective field="payerGroup" headerText="Payer Group" width="180" textAlign="Left" />
            <ColumnDirective field="financialClass" headerText="Financial Class" width="140" textAlign="Left" />
            <ColumnDirective field="ecsPayerId" headerText="ECS Payer ID" width="120" textAlign="Left" />
            <ColumnDirective field="waystarAuthPayer" headerText="Waystar Auth Payer" width="160" textAlign="Left" />
          </ColumnsDirective>
        <Inject services={[Page, Sort]} />
      </GridComponent>

      {/* Edit Dialog */}
      {selectedPayer && (
        <EditPayerDialog
          payer={selectedPayer}
          trigger={
            <button 
              ref={triggerRef}
              style={{ display: 'none' }}
              aria-hidden="true"
            />
          }
        />
      )}
    </Box>
  );
};
